<template>
  <main id="content">
    <section class="hero">
      <b-container>
        <b-row>
          <b-col md class="d-flex align-items-center">
            <div>
              <h1 data-aos="fade-up-right">Reinventing data collection.</h1>
              <p data-aos="fade-right">
                Needle is a versatile and rugged multi-GNSS RTK receiver and
                centimeter-level precision in RTK mode. With boost charge
                technology, built-in radio and bluetooth communication, our
                state-of-the-art cross-platform Orbis app, and Needle's
                lightweight hardware, Needle never shies away from field work.
              </p>
              <div data-aos="fade-up-left">
                <b-btn @click="buy" variant="primary">
                  Preorder Needle
                  <font-awesome-icon icon="arrow-right" />
                </b-btn>
              </div>
            </div>
          </b-col>
          <b-col
            md
            class="d-flex align-items-end justify-content-center pt-5 mt-md-5"
            data-aos="flip-right"
          >
            <img
              alt="Needle device sideview"
              src="../assets/needle-sideview.png"
              class="hero-image"
            />
          </b-col>
        </b-row>
      </b-container>
    </section>
    <section>
      <b-container>
        <b-row class>
          <b-col cols="6" md="4" class="mb-5">
            <div class="h1 mb-3 text-half" data-aos="fade-right">
              <font-awesome-icon icon="globe" />
            </div>
            <h3 class="h5 mb-3" data-aos="fade-right">GNSS</h3>
            <p data-aos="fade-right">
              It harbours extraordinary geo-spatial positioning with global
              coverage using GPS, GLONASS, BeiDou, Galileo, QZSS and SBAS. So,
              no matter where you are, the data is always accurate.
            </p>
          </b-col>
          <b-col cols="6" md="4" class="mb-5">
            <div class="h1 mb-3 text-half" data-aos="fade-down">
              <font-awesome-icon icon="signal" />
            </div>
            <h3 class="h5 mb-3" data-aos="fade-down">Dual Feed Antenna</h3>
            <p data-aos="fade-down">
              Get much higher precision on your location data with lower axial
              ratios and higher multi-path rejection using powerful dual feed
              antennas built into Needle.
            </p>
          </b-col>
          <b-col cols="6" md="4" class="mb-5">
            <div class="h1 mb-3 text-half" data-aos="fade-left">
              <font-awesome-icon icon="battery-full" />
              <span class="ml-2">
                <font-awesome-icon icon="bolt" />
              </span>
            </div>
            <h3 class="h5 mb-3" data-aos="fade-left">Boost Charge</h3>
            <p data-aos="fade-left">
              With a long lasting battery of 12 hours and our revolutionary
              USB-C boost charge technology, you can always be on the go.
            </p>
          </b-col>
          <b-col cols="6" md="4" class="mb-5 mb-md-0">
            <div class="h1 mb-3 text-half" data-aos="fade-right">
              <font-awesome-icon icon="hdd" />
            </div>
            <h3 class="h5 mb-3" data-aos="fade-right">4GB Storage</h3>
            <p data-aos="fade-right">
              Needle comes with 4GB of high performance internal storage to
              collect all your logs and solution tracks.
            </p>
          </b-col>
          <b-col cols="6" md="4">
            <div class="h1 mb-3 text-half" data-aos="fade-down">
              <font-awesome-icon
                :icon="{ prefix: 'fab', iconName: 'bluetooth' }"
              />
            </div>
            <h3 class="h5 mb-3" data-aos="fade-down">High Mobility</h3>
            <p data-aos="fade-down">
              Using long-range radio frequencies stretching upto 8 km and
              consistent Bluetooth 4.0 connectivity, Needle is with you every
              step of the way.
            </p>
          </b-col>
          <b-col cols="6" md="4">
            <div class="h1 mb-3 text-half" data-aos="fade-right">
              <font-awesome-icon icon="cube" />
            </div>
            <h3 class="h5 mb-3" data-aos="fade-right">
              Elegant and Unobstructive
            </h3>
            <p data-aos="fade-right">
              With a beautiful and aesthetic design, Needle is thin and light,
              and showcases fine craftmanship with easy to use hardware.
            </p>
          </b-col>
        </b-row>
      </b-container>
    </section>
    <section class="splash text-center">
      <b-container>
        <b-row align-h="center">
          <b-col cols="8">
            <div>
              <h2 class="h1" data-aos="fade-down">
                Built for action. IP67-certified.
              </h2>
              <p data-aos="fade-down">
                Our IP67 certified receiver lets you dive into action with its
                dust, water and fall resistant architecture. And its lightweight
                design means that Needle will float in water.
              </p>
            </div>
          </b-col>
        </b-row>
        <b-row align-h="center" data-aos="fade-down">
          <b-col cols="6" md="3" class="mt-5">
            <div class="h2">&minus;20&deg;C &ndash; 65&deg;C</div>
            <div>&minus;4&deg;F &ndash; 169&deg;F</div>
          </b-col>
          <b-col cols="6" md="3" class="mt-5">
            <div class="h2">230g</div>
            <div>0.507 lbs</div>
          </b-col>
          <b-col cols="6" md="3" class="mt-5">
            <div class="h2">
              145&times;145&times;85mm
              <sup>3</sup>
            </div>
            <div>
              5.7&times;5.7&times;3.3 inch
              <sup>3</sup>
            </div>
          </b-col>
        </b-row>
      </b-container>
    </section>
    <section class="heading-section">
      <b-container>
        <h2 class="mb-5 text-center" data-aos="fade-down">
          Get started in minutes.
        </h2>
        <b-row class="text-center" data-aos="fade-down">
          <b-col cols="6" md="3" class="mb-4 mb-md-0">
            <img alt="Instruction step 1" src="../assets/needle-step-1.png" />
            <p class="mt-3">
              Hold the power button for 3 seconds to turn Needle base station on
            </p>
          </b-col>
          <b-col cols="6" md="3" class="mb-4 mb-md-0">
            <img alt="Instruction step 1" src="../assets/needle-step-2.png" />
            <p class="mt-3">
              Wait for ~5 minutes Needle base station to acquire GPS fix
            </p>
          </b-col>
          <b-col cols="6" md="3">
            <img alt="Instruction step 1" src="../assets/needle-step-3.png" />
            <p class="mt-3">
              Turn on the rover and it will automatically connect to base
              station
            </p>
          </b-col>
          <b-col cols="6" md="3">
            <img alt="Instruction step 1" src="../assets/needle-step-4.png" />
            <p class="mt-3">
              Connect rover Orbis (cross-platform app) and start collecting data
            </p>
          </b-col>
        </b-row>
      </b-container>
    </section>
    <section class="heading-section gray">
      <b-container>
        <h2 class="mb-5 text-center" data-aos="fade-down">
          Good on paper. Better on the field.
        </h2>
        <b-row class="table-col" data-aos="fade-down">
          <b-col md="4">
            <h3 class="h5 p-3">Mechanical</h3>
            <table class="table">
              <tbody>
                <tr>
                  <td>Size</td>
                  <td>
                    145 &times; 145 &times; 85 mm
                    <sup>3</sup>
                  </td>
                </tr>
                <tr>
                  <td>Weight</td>
                  <td>690 g</td>
                </tr>
                <tr>
                  <td>Operating T&deg;</td>
                  <td>&minus;20&deg;C to +65&deg;C</td>
                </tr>
                <tr>
                  <td>Ingress protection</td>
                  <td>IP67 (water and dustproof)</td>
                </tr>
              </tbody>
            </table>
          </b-col>
          <b-col md="4">
            <h3 class="h5 p-3">Electrical</h3>
            <table class="table">
              <tbody>
                <tr>
                  <td>Battery life</td>
                  <td>12 hours</td>
                </tr>
                <tr>
                  <td>Charging port</td>
                  <td>USB type C</td>
                </tr>
                <tr>
                  <td>External power input</td>
                  <td>5&ndash;40 V</td>
                </tr>
                <tr>
                  <td>Certifications</td>
                  <td>FCC, CE, IP67</td>
                </tr>
              </tbody>
            </table>
          </b-col>
          <b-col md="4">
            <h3 class="h5 p-3">Positioning</h3>
            <table class="table">
              <tbody>
                <tr>
                  <td>Static horizontal</td>
                  <td>5 mm + 1 ppm</td>
                </tr>
                <tr>
                  <td>Static vertical</td>
                  <td>10 mm + 2 ppm</td>
                </tr>
                <tr>
                  <td>Kinematic horizontal</td>
                  <td>7 mm + 1 ppm</td>
                </tr>
                <tr>
                  <td>Kinematic vertical</td>
                  <td>14 mm + 2 ppm</td>
                </tr>
              </tbody>
            </table>
          </b-col>
          <b-col md="4">
            <h3 class="h5 p-3">Data &amp; Connectivity</h3>
            <table class="table">
              <tbody>
                <tr>
                  <td>Radio</td>
                  <td>433/915 MHz</td>
                </tr>
                <tr>
                  <td>Bluetooth</td>
                  <td>4.0</td>
                </tr>
                <tr>
                  <td>Signals</td>
                  <td>GPS, GLONASS, GALILEO, BEIDOU</td>
                </tr>
                <tr>
                  <td>Tracking channels</td>
                  <td>72</td>
                </tr>
                <tr>
                  <td>Update rate</td>
                  <td>4-10hz</td>
                </tr>
              </tbody>
            </table>
          </b-col>
          <b-col md="4">
            <h3 class="h5 p-3">GNSS</h3>
            <table class="table">
              <tbody>
                <tr>
                  <td>Static horizontal</td>
                  <td>5 mm + 1 ppm</td>
                </tr>
                <tr>
                  <td>Static vertical</td>
                  <td>10 mm + 2 ppm</td>
                </tr>
                <tr>
                  <td>Kinematic horizontal</td>
                  <td>7 mm + 1 ppm</td>
                </tr>
                <tr>
                  <td>Kinematic vertical</td>
                  <td>14 mm + 2 ppm</td>
                </tr>
              </tbody>
            </table>
          </b-col>
          <b-col md="4">
            <h3 class="h5 p-3">Horizontal position accuracy</h3>
            <table class="table">
              <tbody>
                <tr>
                  <td>Standalone</td>
                  <td>2.5 m CEP</td>
                </tr>
                <tr>
                  <td>RTK</td>
                  <td>0.025 m + 1 ppm CEP</td>
                </tr>
              </tbody>
            </table>
          </b-col>
        </b-row>
        <div>
          <h3 class="h5 p-3">Time-to-first-fix</h3>
          <table class="table">
            <thead>
              <tr>
                <td></td>
                <th>GPS &amp; GLONASS</th>
                <th>GPS &amp; BeiDou</th>
                <th>GPS</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Cold start</td>
                <td>26s</td>
                <td>28s</td>
                <td>29s</td>
              </tr>
              <tr>
                <td>Hot start</td>
                <td>1s</td>
                <td>1s</td>
                <td>1s</td>
              </tr>
              <tr>
                <td>Aided start</td>
                <td>2s</td>
                <td>3s</td>
                <td>2s</td>
              </tr>
            </tbody>
          </table>
        </div>
      </b-container>
    </section>
    <section class="cta text-center pl-4 pr-4">
      <h2 data-aos="fade-down">Ready? Get Needle now.</h2>
      <p data-aos="fade-down">
        Order now for an exclusive introductory price. Ships July 2018.
      </p>
      <div class="mt-4">
        <b-btn
          id="btnNeedlePreorder"
          @click="buy"
          variant="primary"
          class="btn-lg"
        >
          Preorder Needle
          <font-awesome-icon icon="arrow-right" />
        </b-btn>
      </div>
    </section>
  </main>
</template>

<script>
import { mapGetters } from "vuex";
import router from "../modules/router";
import needleProductImage from "../assets/needle-image.png";

export default {
  data() {
    return {
      needleProductImage
    };
  },
  computed: {
    ...mapGetters({
      user: "getUser"
    })
  },
  methods: {
    tryBuy() {
      if (this.user) {
        router.push("/settings/subscriptions");
      } else {
        this.$root.$children[0].visibleModal = "auth";
      }
    },
    buy() {
      this.$store.dispatch({
        type: "addToCart",
        product: {
          productId: "needle",
          name: "Needle (Explorer edition)",
          qty: 1,
          desc:
            "Versatile and rugged multi-GNSS RTK receiver and centimeter-level precision in RTK mode.",
          priceBefore: 150000,
          price: 100000,
          imageUrl: this.needleProductImage,
          footnote: "Ships July 2018"
        }
      });
      router.push("/cart");
    }
  }
};
</script>

<style lang="scss" scoped>
section {
  &.hero {
    color: #fff;
    background: linear-gradient(
        to right,
        rgba(0, 0, 0, 0.5),
        rgba(0, 0, 0, 0.3)
      ),
      url("https://source.unsplash.com/OJX7gIU3E6U/1600x900") no-repeat center
        center;
    background-size: cover;
    padding: 0;
    p {
      margin: 1rem 0 1.75rem 0;
    }
  }
  &.splash {
    color: #fff;
    background: linear-gradient(
        to right,
        rgba(0, 0, 0, 0.6),
        rgba(0, 0, 0, 0.6)
      ),
      url("../assets/needle-splash-bg.png") no-repeat center center;
    background-size: cover;
  }
  &.cta {
    color: #fff;
    background: linear-gradient(
        to right,
        rgba(0, 0, 0, 0.6),
        rgba(0, 0, 0, 0.6)
      ),
      url("../assets/needle-cta-bg.png") no-repeat center center;
    background-size: cover;
  }
}
@media (max-width: 750px) {
  section.hero {
    padding-top: 3rem;
  }
  .hero-image {
    max-height: 250px;
  }
}
.table-col td:nth-child(2) {
  text-align: right;
}
</style>
<style>
.artibot-wrapper--19YJW {
  display: show;
}
</style>
